// src/components/admin/AccountsFieldDefinitions.js
// ✅ DROP-IN: Matches InventoryFieldDefinitions look/behavior 1:1
// - Same header + icon, info box, New Field toggle, create form layout
// - Same table layout with view-mode rows + inline edit-mode rows
// - Same key normalization + sorting by order then label
// - Account-specific visibility toggles: Table / Panel / Filters

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Settings2, Plus, Trash2, Edit2, Save, X, Info } from "lucide-react";

const AccountsFieldDefinitions = ({ companyId }) => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Create form
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    key: "",
    label: "",
    type: "text", // text | number | boolean | select
    showInTable: true,
    showInAccountPanel: true,
    showInFilters: false,
    optionsText: "",
  });

  // Edit form
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      await loadDefinitions(companyId);
      setLoading(false);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadDefinitions = async (compId) => {
    try {
      const colRef = collection(db, "companies", compId, "accountFieldDefinitions");
      const snap = await getDocs(colRef);
      const list = snap.docs.map((d) => {
        const data = d.data() || {};
        return {
          id: d.id,
          key: data.key || "",
          label: data.label || "",
          type: data.type || data.fieldType || "text",
          options: Array.isArray(data.options) ? data.options : [],
          showInTable: Boolean(data.showInTable),
          showInAccountPanel: Boolean(data.showInAccountPanel),
          showInFilters: Boolean(data.showInFilters),
          order: typeof data.order === "number" ? data.order : 9999,
        };
      });

      // Sort by 'order' then label (same behavior as inventory)
      list.sort((a, b) => {
        const orderA = typeof a.order === "number" ? a.order : 9999;
        const orderB = typeof b.order === "number" ? b.order : 9999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.label || "")
          .toLowerCase()
          .localeCompare((b.label || "").toLowerCase());
      });

      setDefinitions(list);
    } catch (err) {
      console.error("[AccountsFieldDefinitions] Error loading:", err);
      setDefinitions([]);
      setError("Failed to load account field definitions.");
    }
  };

  const normalizeKey = (value) => {
    return (value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "_");
  };

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) return;

    setSaving(true);
    setError("");

    try {
      const label = (createForm.label || "").trim();
      let key = normalizeKey(createForm.key || createForm.label);

      if (!label) {
        setError("Label is required.");
        setSaving(false);
        return;
      }
      if (!key) {
        setError("Key is required.");
        setSaving(false);
        return;
      }

      const keyExists = definitions.some(
        (d) => (d.key || "").toLowerCase() === key.toLowerCase()
      );
      if (keyExists) {
        setError("A field with that key already exists.");
        setSaving(false);
        return;
      }

      const options =
        createForm.type === "select"
          ? (createForm.optionsText || "")
              .split(",")
              .map((o) => o.trim())
              .filter(Boolean)
          : [];

      const order = definitions.length
        ? Math.max(
            ...definitions.map((d) => (typeof d.order === "number" ? d.order : 0))
          ) + 1
        : 1;

      const data = {
        key,
        label,
        type: createForm.type,
        showInTable: Boolean(createForm.showInTable),
        showInAccountPanel: Boolean(createForm.showInAccountPanel),
        showInFilters: Boolean(createForm.showInFilters),
        options,
        order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const colRef = collection(db, "companies", companyId, "accountFieldDefinitions");
      await addDoc(colRef, data);

      setCreateForm({
        key: "",
        label: "",
        type: "text",
        showInTable: true,
        showInAccountPanel: true,
        showInFilters: false,
        optionsText: "",
      });
      setCreating(false);

      await loadDefinitions(companyId);
    } catch (err) {
      console.error("[AccountsFieldDefinitions] Error creating:", err);
      setError("Failed to create field definition.");
    }

    setSaving(false);
  };

  const handleBeginEdit = (def) => {
    setError("");
    setEditId(def.id);
    setEditForm({
      key: def.key || "",
      label: def.label || "",
      type: def.type || "text",
      showInTable: Boolean(def.showInTable),
      showInAccountPanel: Boolean(def.showInAccountPanel),
      showInFilters: Boolean(def.showInFilters),
      optionsText: (def.options || []).join(", "),
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setEditForm(null);
  };

  const handleSaveEdit = async (def) => {
    if (!companyId || !editForm) return;

    setSaving(true);
    setError("");

    try {
      const label = (editForm.label || "").trim();
      let key = normalizeKey(editForm.key || editForm.label);

      if (!label) {
        setError("Label is required.");
        setSaving(false);
        return;
      }
      if (!key) {
        setError("Key is required.");
        setSaving(false);
        return;
      }

      const keyExists = definitions.some(
        (d) => d.id !== def.id && (d.key || "").toLowerCase() === key.toLowerCase()
      );
      if (keyExists) {
        setError("Another field already uses that key.");
        setSaving(false);
        return;
      }

      const options =
        editForm.type === "select"
          ? (editForm.optionsText || "")
              .split(",")
              .map((o) => o.trim())
              .filter(Boolean)
          : [];

      const data = {
        key,
        label,
        type: editForm.type,
        showInTable: Boolean(editForm.showInTable),
        showInAccountPanel: Boolean(editForm.showInAccountPanel),
        showInFilters: Boolean(editForm.showInFilters),
        options,
        updatedAt: serverTimestamp(),
      };

      const defRef = doc(db, "companies", companyId, "accountFieldDefinitions", def.id);
      await updateDoc(defRef, data);

      setEditId(null);
      setEditForm(null);
      await loadDefinitions(companyId);
    } catch (err) {
      console.error("[AccountsFieldDefinitions] Error updating:", err);
      setError("Failed to update field definition.");
    }

    setSaving(false);
  };

  const handleDelete = async (def) => {
    if (!companyId) return;

    const confirmDelete = window.confirm(
      `Delete field "${def.label || def.key}"?\n\nThis won't remove data from existing accounts, but the field will no longer appear in the UI.`
    );
    if (!confirmDelete) return;

    setSaving(true);
    setError("");

    try {
      const defRef = doc(db, "companies", companyId, "accountFieldDefinitions", def.id);
      await deleteDoc(defRef);
      await loadDefinitions(companyId);
    } catch (err) {
      console.error("[AccountsFieldDefinitions] Error deleting:", err);
      setError("Failed to delete field definition.");
    }

    setSaving(false);
  };

  if (!companyId) {
    return (
      <div className="text-sm text-slate-500">
        Company not loaded yet. Try again in a moment.
      </div>
    );
  }

  if (loading) {
    return <div className="text-sm text-slate-600">Loading account fields…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-blue-50 text-blue-700">
            <Settings2 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold text-slate-900">
              Account Field Definitions
            </h2>
            <p className="text-xs text-slate-500">
              Define extra fields for accounts (cuisine type, license type, tap
              count, etc.). Values are stored on each account in{" "}
              <code className="bg-slate-100 px-1 rounded text-[11px]">
                custom[key]
              </code>
              .
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setCreating((prev) => !prev)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs md:text-sm font-medium rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
        >
          <Plus className="w-4 h-4" />
          <span>{creating ? "Close" : "New Field"}</span>
        </button>
      </div>

      {/* Info / tips */}
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
        <Info className="w-4 h-4 mt-0.5 text-slate-400" />
        <div>
          <p>
            Core account fields like name, address, phone, email, and notes are built
            in. Use these custom fields for company-specific attributes. You can
            control whether each field appears in the Accounts table, Account panel,
            or as a filter.
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-lg text-xs">
          {error}
        </div>
      )}

      {/* Create form */}
      {creating && (
        <div className="border border-slate-200 rounded-xl p-3 md:p-4 bg-slate-50/60 space-y-3">
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Add New Field</h3>

          <form onSubmit={handleCreateSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Label */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Label *</label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={createForm.label}
                onChange={(e) => handleCreateChange("label", e.target.value)}
                placeholder="e.g., Cuisine Type, Tap Count"
                required
              />
            </div>

            {/* Key */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Key (no spaces)
              </label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={createForm.key}
                onChange={(e) => handleCreateChange("key", e.target.value)}
                placeholder="e.g., cuisine_type, tap_count"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">
                If left blank, it will be generated from the label.
              </p>
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Type</label>
              <select
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
                value={createForm.type}
                onChange={(e) => handleCreateChange("type", e.target.value)}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="boolean">Yes / No</option>
                <option value="select">Select (dropdown)</option>
              </select>
            </div>

            {/* Options */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Options (for select type)
              </label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={createForm.optionsText}
                onChange={(e) => handleCreateChange("optionsText", e.target.value)}
                placeholder="Comma-separated, e.g., Italian, Mexican, American"
                disabled={createForm.type !== "select"}
              />
            </div>

            {/* Visibility toggles */}
            <div className="flex flex-wrap items-center gap-3 col-span-1 md:col-span-2">
              <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600"
                  checked={createForm.showInTable}
                  onChange={(e) => handleCreateChange("showInTable", e.target.checked)}
                />
                Show in Accounts Table
              </label>

              <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600"
                  checked={createForm.showInAccountPanel}
                  onChange={(e) =>
                    handleCreateChange("showInAccountPanel", e.target.checked)
                  }
                />
                Show in Account Panel
              </label>

              <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600"
                  checked={createForm.showInFilters}
                  onChange={(e) => handleCreateChange("showInFilters", e.target.checked)}
                />
                Show as Filter
              </label>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 col-span-1 md:col-span-2 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : "Create Field"}
              </button>

              <button
                type="button"
                onClick={() => setCreating(false)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Definitions table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <table className="min-w-full border-collapse text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Key</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Visibility</th>
              <th className="px-3 py-2">Options</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {definitions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-xs text-slate-500">
                  No custom fields yet. Click <strong>New Field</strong> to add one.
                </td>
              </tr>
            ) : (
              definitions.map((def) => {
                const isEditing = editId === def.id;

                if (!isEditing) {
                  return (
                    <tr key={def.id} className="border-t border-slate-200 hover:bg-slate-50/50">
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-slate-900">{def.label || def.key}</div>
                      </td>

                      <td className="px-3 py-2 align-top text-slate-600">
                        <code className="text-[11px] bg-slate-100 rounded px-1.5 py-0.5">
                          {def.key}
                        </code>
                      </td>

                      <td className="px-3 py-2 align-top text-slate-600 capitalize">
                        {def.type || "text"}
                      </td>

                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1 text-[11px] text-slate-600">
                          {def.showInTable && (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                              Table
                            </span>
                          )}
                          {def.showInAccountPanel && (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                              Panel
                            </span>
                          )}
                          {def.showInFilters && (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                              Filters
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-2 align-top text-slate-600">
                        {(def.options || []).length > 0 ? (
                          <span className="text-[11px]">{def.options.join(", ")}</span>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>

                      <td className="px-3 py-2 align-top text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleBeginEdit(def)}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
                            title="Edit field"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(def)}
                            className="p-1.5 rounded-md hover:bg-red-50 text-red-600"
                            title="Delete field"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                // Editing row
                return (
                  <tr key={def.id} className="border-t border-slate-200 bg-slate-50/70">
                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs"
                        value={editForm?.label || ""}
                        onChange={(e) => handleEditChange("label", e.target.value)}
                        placeholder="Label"
                      />
                    </td>

                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs"
                        value={editForm?.key || ""}
                        onChange={(e) => handleEditChange("key", e.target.value)}
                        placeholder="key_name"
                      />
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Used as <code>custom[key]</code> on accounts.
                      </p>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <select
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs bg-white"
                        value={editForm?.type || "text"}
                        onChange={(e) => handleEditChange("type", e.target.value)}
                      >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="boolean">Yes / No</option>
                        <option value="select">Select</option>
                      </select>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1 text-[11px] text-slate-700">
                        <label className="inline-flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 text-blue-600"
                            checked={!!editForm?.showInTable}
                            onChange={(e) => handleEditChange("showInTable", e.target.checked)}
                          />
                          Table
                        </label>

                        <label className="inline-flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 text-blue-600"
                            checked={!!editForm?.showInAccountPanel}
                            onChange={(e) =>
                              handleEditChange("showInAccountPanel", e.target.checked)
                            }
                          />
                          Panel
                        </label>

                        <label className="inline-flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 text-blue-600"
                            checked={!!editForm?.showInFilters}
                            onChange={(e) => handleEditChange("showInFilters", e.target.checked)}
                          />
                          Filters
                        </label>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs"
                        value={editForm?.optionsText || ""}
                        onChange={(e) => handleEditChange("optionsText", e.target.value)}
                        placeholder="Comma-separated options"
                        disabled={editForm?.type !== "select"}
                      />
                    </td>

                    <td className="px-3 py-2 align-top text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(def)}
                          disabled={saving}
                          className="inline-flex items-center justify-center p-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                          title="Save"
                        >
                          <Save className="w-4 h-4" />
                        </button>

                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="inline-flex items-center justify-center p-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountsFieldDefinitions;
